'use client';

import { createContext, useContext, useEffect, useState, ReactNode } from 'react';

type Language = 'en' | 'ar';

interface Translations {
  [key: string]: string;
}

const translations: Record<Language, Translations> = {
  en: {
    // Header
    'nav.categories': 'Categories',
    'nav.countries': 'Countries',
    'nav.about': 'About',
    'nav.pricing': 'Pricing',
    'nav.listBusiness': 'List Your Business',

    // Hero
    'hero.title': 'Find Trusted Business Service Providers',
    'hero.subtitle': 'Connect with verified professionals across the GCC region',
    'hero.searchPlaceholder': 'Search services, providers, or categories...',
    'hero.search': 'Search',

    // Categories
    'categories.title': 'Browse by Category',
    'categories.subtitle': 'Find the right service for your business needs',

    // Countries
    'countries.title': 'Providers by Country',
    'countries.subtitle': 'Find services in your region',

    // Featured
    'featured.title': 'Featured Premium Providers',
    'featured.subtitle': 'Verified and trusted business service providers',

    // How it works
    'howItWorks.title': 'How It Works',
    'howItWorks.subtitle': 'Simple steps to find the perfect business service provider',
    'howItWorks.step1.title': 'Search for Service',
    'howItWorks.step1.desc': 'Select your service type and location from our comprehensive directory',
    'howItWorks.step2.title': 'Compare Providers',
    'howItWorks.step2.desc': 'Review ratings, services, and expertise of verified providers',
    'howItWorks.step3.title': 'Request Free Quotes',
    'howItWorks.step3.desc': 'Contact multiple providers and get detailed proposals at no cost',
    'howItWorks.step4.title': 'Choose Best Fit',
    'howItWorks.step4.desc': 'Select the provider that best meets your business needs',

    // List business
    'listBusiness.title': 'Are You a Service Provider?',
    'listBusiness.subtitle': 'Join GCC Business Hub and connect with thousands of potential clients',
    'listBusiness.feature1': 'Free Listing',
    'listBusiness.feature2': 'Verified Badge',
    'listBusiness.feature3': 'Lead Generation',
    'listBusiness.cta': 'List Your Business Free',

    // Footer
    'footer.tagline': 'Your trusted directory for business services across the GCC region.',
    'footer.categories': 'Categories',
    'footer.countries': 'Countries',
    'footer.company': 'Company',
    'footer.aboutUs': 'About Us',
    'footer.contact': 'Contact',
    'footer.privacy': 'Privacy Policy',
    'footer.terms': 'Terms of Service',
    'footer.copyright': '2025 GCC Business Hub. All rights reserved.',
    'footer.poweredBy': 'Powered by',

    // Provider Card
    'provider.viewDetails': 'View Details',
    'provider.featured': 'FEATURED',
    'provider.pro': 'PRO',
    'provider.premium': 'PREMIUM',
    'provider.enterprise': 'ENTERPRISE',
    'provider.verified': 'Verified',
    'provider.contactAvailable': 'Contact details available after inquiry',
    'provider.visitWebsite': 'Visit Website',
    'provider.services': 'Services:',

    // Countries
    'country.BH': 'Bahrain',
    'country.AE': 'United Arab Emirates',
    'country.SA': 'Saudi Arabia',
    'country.QA': 'Qatar',
    'country.KW': 'Kuwait',
    'country.OM': 'Oman',
  },
  ar: {
    // Header
    'nav.categories': 'الفئات',
    'nav.countries': 'الدول',
    'nav.about': 'عن المنصة',
    'nav.pricing': 'الأسعار',
    'nav.listBusiness': 'أضف شركتك',

    // Hero
    'hero.title': 'اعثر على مزودي خدمات الأعمال الموثوقين',
    'hero.subtitle': 'تواصل مع محترفين معتمدين في دول مجلس التعاون الخليجي',
    'hero.searchPlaceholder': 'ابحث عن الخدمات، المزودين، أو الفئات...',
    'hero.search': 'بحث',

    // Categories
    'categories.title': 'تصفح حسب الفئة',
    'categories.subtitle': 'اعثر على الخدمة المناسبة لاحتياجات عملك',

    // Countries
    'countries.title': 'مزودون حسب الدولة',
    'countries.subtitle': 'اعثر على الخدمات في منطقتك',

    // Featured
    'featured.title': 'مزودون مميزون',
    'featured.subtitle': 'مزودو خدمات أعمال معتمدون وموثوقون',

    // How it works
    'howItWorks.title': 'كيف يعمل',
    'howItWorks.subtitle': 'خطوات بسيطة للعثور على مزود خدمة الأعمال المثالي',
    'howItWorks.step1.title': 'ابحث عن الخدمة',
    'howItWorks.step1.desc': 'حدد نوع الخدمة والموقع من دليلنا الشامل',
    'howItWorks.step2.title': 'قارن المزودين',
    'howItWorks.step2.desc': 'راجع التقييمات والخدمات والخبرة للمزودين المعتمدين',
    'howItWorks.step3.title': 'اطلب عروض أسعار مجانية',
    'howItWorks.step3.desc': 'تواصل مع عدة مزودين واحصل على مقترحات مفصلة دون تكلفة',
    'howItWorks.step4.title': 'اختر الأنسب',
    'howItWorks.step4.desc': 'اختر المزود الذي يلبي احتياجات عملك بشكل أفضل',

    // List business
    'listBusiness.title': 'هل أنت مزود خدمة؟',
    'listBusiness.subtitle': 'انضم إلى مركز أعمال دول مجلس التعاون وتواصل مع آلاف العملاء المحتملين',
    'listBusiness.feature1': 'قائمة مجانية',
    'listBusiness.feature2': 'شارة معتمدة',
    'listBusiness.feature3': 'توليد عملاء محتملين',
    'listBusiness.cta': 'أضف شركتك مجاناً',

    // Footer
    'footer.tagline': 'دليلك الموثوق لخدمات الأعمال في دول مجلس التعاون الخليجي.',
    'footer.categories': 'الفئات',
    'footer.countries': 'الدول',
    'footer.company': 'الشركة',
    'footer.aboutUs': 'عن المنصة',
    'footer.contact': 'اتصل بنا',
    'footer.privacy': 'سياسة الخصوصية',
    'footer.terms': 'شروط الخدمة',
    'footer.copyright': '© 2025 مركز أعمال دول مجلس التعاون. جميع الحقوق محفوظة.',
    'footer.poweredBy': 'مدعوم من',

    // Provider Card
    'provider.viewDetails': 'عرض التفاصيل',
    'provider.featured': 'مميز',
    'provider.pro': 'محترف',
    'provider.premium': 'ممتاز',
    'provider.enterprise': 'مؤسسي',
    'provider.verified': 'معتمد',
    'provider.contactAvailable': 'تفاصيل الاتصال متاحة بعد الاستفسار',
    'provider.visitWebsite': 'زيارة الموقع',
    'provider.services': 'الخدمات:',

    // Countries
    'country.BH': 'البحرين',
    'country.AE': 'الإمارات العربية المتحدة',
    'country.SA': 'المملكة العربية السعودية',
    'country.QA': 'قطر',
    'country.KW': 'الكويت',
    'country.OM': 'سلطنة عمان',
  },
};

interface LanguageContextType {
  language: Language;
  toggleLanguage: () => void;
  t: (key: string) => string;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export function LanguageProvider({ children }: { children: ReactNode }) {
  const [language, setLanguage] = useState<Language>('en');
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
    const savedLanguage = localStorage.getItem('language') as Language | null;
    if (savedLanguage) {
      setLanguage(savedLanguage);
      updateHtmlDirection(savedLanguage);
    }
  }, []);

  const updateHtmlDirection = (lang: Language) => {
    document.documentElement.dir = lang === 'ar' ? 'rtl' : 'ltr';
    document.documentElement.lang = lang;
  };

  const toggleLanguage = () => {
    const newLanguage = language === 'en' ? 'ar' : 'en';
    setLanguage(newLanguage);
    localStorage.setItem('language', newLanguage);
    updateHtmlDirection(newLanguage);
  };

  const t = (key: string): string => {
    return translations[language][key] || key;
  };

  if (!mounted) {
    return <>{children}</>;
  }

  return (
    <LanguageContext.Provider value={{ language, toggleLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    // Return default values for SSR/SSG
    return {
      language: 'en' as Language,
      toggleLanguage: () => {},
      t: (key: string) => translations.en[key] || key,
    };
  }
  return context;
}
